import React from 'react';
import TypingSpeedClassifications from './TypingSpeedClassifications';
import '../styles/TypingTestExtras.css';
import { FaCheckCircle } from 'react-icons/fa';
import styled from 'styled-components';

const Section = styled.section`
  padding: 50px 20px;
  background-color: var(--background-color-container);
`;
const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
`;

const richIntroContent = {
  15: {
    h2: 'The Zippy 15-Second Typing Sprint!',
    intro: `Hello there, future typing ace! Ever dreamed of your fingers dancing across the keyboard? Our 15-second typing test is your fun-sized entry ticket. It's quick, it's engaging, and honestly, it's a neat way to see just how speedy you can be. Ready to give those keys a workout?`,
    sections: [
      { h3: 'Why Just 15 Seconds, You Ask?', p: `Think of it as a typing espresso shot! Short bursts are great for focus and stopping your brain (and fingers) from going on strike. In 15 tiny seconds, you get the lowdown on your words-per-minute (WPM) and accuracy. It's a brilliant little progress tracker and a perfect, playful break in your day. Who knew 15 seconds could be so revealing?` },
      { h3: 'How Does This Magic Work?', p: `It's as easy as pie (a very fast pie). Settle in, get those fingers on their starting blocks (aka the home row). The moment you type the first letter of the given text, boom! The 15-second countdown is on. Your quest? Type the words quickly and correctly. When the timer dings, you'll see your stats. Use them to aim for a new personal best – you got this!` },
      { h3: 'Pro Tips for Typing Triumph:', p: `A little finger warm-up never hurt anyone – maybe some air-typing to your favorite tune? Keep your hands and posture relaxed; tension is the enemy of speed! Like any good skill, practice is your secret weapon. A little bit each day can make a surprisingly big difference. You might just amaze yourself!` },
      { h3: 'Share the Fun, Double the Gains!', p: `Why not nudge a friend to try it too? A little friendly rivalry can be wonderfully motivating. Plus, tracking your scores and seeing them climb is a pretty sweet feeling. Our 15-second sprint is a star, but we have other time options if you're feeling adventurous!` },
      { h3: 'Ready to Unleash Your Inner Speedster?', p: `Just scroll on up to start your 15-second challenge. Let's see what those fingers can do! Happy typing, and remember: every tap is a step closer to typing greatness. Go on, be awesome!` },
    ]
  },
  30: {
    h2: 'The 30-Second Typing Dash!',
    intro: `Feeling warmed up? Excellent! The 30-second dash is the perfect next step. It gives you just enough time to hit your stride and show those keys who's boss. Let's see what a little more time does for your speed!`,
    sections: [
      { h3: 'So, Why 30 Seconds?', p: `This is the sweet spot. It's long enough to get into a real typing rhythm but short enough that you won't need a snack break midway through. You get a solid, reliable score that really shows off your skills while keeping things fast and fun.` },
      { h3: 'Here’s the Game Plan:', p: `You know the drill! Get comfortable, fingers poised for action. Once you start, the clock is ticking for 30 seconds. Focus on flowing through the words smoothly and accurately. Your final score will give you a great snapshot of your current abilities.` },
      { h3: 'Tips for Nailing the Dash:', p: `Rhythm is your best friend here. Try to find a steady, consistent pace. Don't forget to breathe! Holding your breath is a classic rookie move that, surprisingly, does not make you type faster. Keep it relaxed and steady.` },
      { h3: 'Bring Your Friends!', p: `Everything's better with company! Challenge your friends to the 30-second dash and see whose fingers reign supreme. Keeping track of everyone's progress is a fantastic motivator. Plus, you get bragging rights.` },
      { h3: 'Ready for Your Close-Up?', p: `Time to shine! Scroll to the top and let the 30-second challenge begin. Show us that rhythm and flow. Happy typing, and may your accuracy be ever in your favor!` },
    ]
  },
  60: {
    h2: 'The Classic 1-Minute Typing Challenge!',
    intro: `Welcome to the main event! The 1-minute typing test is the gold standard, the one that separates the contenders from the champions. It's a true test of your speed and focus. Are you ready to set a new personal record?`,
    sections: [
      { h3: 'What’s Special About a Minute?', p: `A full minute gives you the space to truly settle in. Any early jitters have time to fade, and your real, consistent typing speed can shine through. It's the most popular test for a reason – it provides a solid, respectable benchmark of your skill.` },
      { h3: 'Your Mission, Should You Choose It:', p: `Find your happy typing place, fingers on the home row. The timer starts with your first keystroke. For the next 60 seconds, your world is nothing but letters and spaces. Focus on accuracy first, then let the speed follow. Your final WPM will be one to remember!` },
      { h3: 'Secrets to a Great Score:', p: `Consistency is key. It's better to maintain a steady, accurate pace than to sprint and make mistakes. If you do make a typo, don't panic! Just shake it off and keep going. A calm mind leads to nimble fingers.` },
      { h3: 'Make it a Team Sport:', p: `The 1-minute challenge is the perfect benchmark to compare with friends and colleagues. Set up a leaderboard and see who comes out on top! Celebrating improvements together makes the journey even more rewarding.` },
      { h3: 'Your Minute of Fame Awaits!', p: `This is it! Scroll up and prepare for 60 seconds of typing glory. Take a breath, relax your shoulders, and let's do this. Good luck, and may the WPM be with you!` },
    ]
  },
  180: {
    h2: 'The 3-Minute Endurance Run!',
    intro: `Ready to test your typing muscles? The 3-minute challenge is where consistency and endurance take center stage. This isn't just a sprint; it's a steady journey to see what you're truly capable of. Let's dig in!`,
    sections: [
      { h3: 'Why Go for 3 Whole Minutes?', p: `This is where you find your zen typing state. Three minutes is enough time to overcome the initial rush and settle into a long-term rhythm. It tests your ability to stay focused and maintain accuracy when your fingers might be starting to complain just a little.` },
      { h3: 'The Endurance Test Plan:', p: `As always, posture is your friend. Sit up straight and give your fingers a good starting position. Once you begin, you're in it for 180 seconds. The goal is to keep a smooth and even pace from start to finish. This is all about that sustained focus.` },
      { h3: 'Tips for Going the Distance:', p: `Pacing is everything. Don't try to go full-speed the entire time; you'll burn out. Find a comfortable, quick rhythm you can hold. If you make a mistake, correct it calmly and carry on. Don't let one typo have a party on your screen.` },
      { h3: 'For the Dedicated Typist:', p: `Completing the 3-minute test is a great achievement that shows real dedication. Share your score with pride! It's a true measure of typing fitness that many others don't attempt. You're in a special club now!` },
      { h3: 'Ready to Prove Your Stamina?', p: `Okay, deep breath. You've trained for this (you have, right?). Scroll to the top and begin your endurance run. We're rooting for you all the way. Stay strong, stay focused, and happy typing!` },
    ]
  },
  300: {
    h2: 'The Epic 5-Minute Typing Marathon!',
    intro: `Welcome, brave soul, to the Mount Everest of our typing tests! The 5-minute marathon is the ultimate challenge of skill, focus, and pure typing stamina. Only the most determined attempt it. Are you one of them?`,
    sections: [
      { h3: '5 Minutes? Are You Serious?', p: `Oh, we're serious. Five minutes is enough time to write a short story, order a pizza, and still have a few seconds to question your life choices. But seriously, it's the most accurate way to measure your true, sustained typing speed, free of any short-sprint luck.` },
      { h3: 'The Marathoner’s Guide:', p: `This is a long haul, so get truly comfortable. Check your posture. The clock starts when you do. Your goal is simple but challenging: maintain your focus and accuracy for the entire 300 seconds. This is as real as it gets.` },
      { h3: 'How to Survive and Thrive:', p: `Remember to breathe! And blink! It's easy to forget the basics when you're in the zone. Keep your pace steady and comfortable. Your back and wrists will thank you for maintaining good posture. This is a mental game as much as a physical one.` },
      { h3: 'A Legendary Achievement:', p: `Just finishing the 5-minute marathon is a victory in itself, so give yourself a pat on the back no matter your score. This is a feat of endurance. Sharing this score doesn't just earn you bragging rights; it earns you respect.` },
      { h3: 'Your Ultimate Challenge Begins Now!', p: `This is the final boss. Scroll up to the top and begin your epic quest. May your fingers be swift and your backspace key be lonely. We believe in you. Go forth and conquer!` },
    ]
  }
};

const TypingTestExtras = ({ duration, onStartTest }) => {
  const richIntro = richIntroContent[duration];

  if (duration === 15) {
    return (
      <>
        {/* Features Section - Custom for 15s */}
        <div className="features-wrapper">
          <section className="features-section">
            <h5>Unlock Your Typing Potential</h5>
            <h2>Our features give you the flexibility and insights to master typing.</h2>
            <div className="features-container">
              <div className="feature-box">
                <FaCheckCircle style={{ marginTop: 4, color: 'var(--hover-border-color)', marginRight: 15 }} />
                <p><span>Easy Analytics:</span> Progress with simple analytics. See your strengths and areas for improvement at a glance.</p>
              </div>
              <div className="feature-box">
                <FaCheckCircle style={{ marginTop: 4, color: 'var(--hover-border-color)', marginRight: 15 }} />
                <p><span>Unlimited Practice:</span> Improve your typing speed and accuracy with endless practice opportunities.</p>
              </div>
              <div className="feature-box">
                <FaCheckCircle style={{ marginTop: 4, color: 'var(--hover-border-color)', marginRight: 15 }} />
                <p><span>Instant Feedback:</span> Get instant feedback on your typing. Quickly identify and correct mistakes to improve faster.</p>
              </div>
              <div className="feature-box">
                <FaCheckCircle style={{ marginTop: 4, color: 'var(--hover-border-color)', marginRight: 15 }} />
                <p><span>Flexible Time Limits:</span> Choose from a variety of test durations to match your pace and goals.</p>
              </div>
            </div>
          </section>
        </div>
        {/* Rich Intro Section for 15s */}
        {richIntro && (
          <div className="wrapper-intro">
            <h2>{richIntro.h2}</h2>
            <p className="intro">{richIntro.intro}</p>
            {richIntro.sections.map((sec, idx) => (
              <React.Fragment key={idx}>
                <h3>{sec.h3}</h3>
                <p>{sec.p}</p>
              </React.Fragment>
            ))}
          </div>
        )}
        <TypingSpeedClassifications />
      </>
    );
  }

  return (
    <>
      {/* Features Section */}
      <div className="features-wrapper">
        <section className="features-section">
          <h5>Unlock Your Typing Potential</h5>
          <h2>Our features give you the flexibility and insights to master typing.</h2>
          <div className="features-container">
            <div className="feature-box">
              <FaCheckCircle />
              <p><span>Easy Analytics:</span> Progress with simple analytics. See your strengths and areas for improvement at a glance.</p>
            </div>
            <div className="feature-box">
              <FaCheckCircle />
              <p><span>Unlimited Practice:</span> Take as many tests as you like to continuously improve your skills.</p>
            </div>
            <div className="feature-box">
              <FaCheckCircle />
              <p><span>Instant Feedback:</span> Receive immediate feedback on your typing performance.</p>
            </div>
            <div className="feature-box">
              <FaCheckCircle />
              <p><span>Flexible Time Limits:</span> Choose from a variety of test durations to suit your needs and goals.</p>
            </div>
          </div>
        </section>
      </div>
      {/* Rich Intro Section for all durations */}
      {richIntro && (
        <div className="wrapper-intro">
          <h2>{richIntro.h2}</h2>
          <p className="intro">{richIntro.intro}</p>
          {richIntro.sections.map((sec, idx) => (
            <React.Fragment key={idx}>
              <h3>{sec.h3}</h3>
              <p>{sec.p}</p>
            </React.Fragment>
          ))}
        </div>
      )}
      <TypingSpeedClassifications />
    </>
  );
};

export default TypingTestExtras; 